import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MergeStrategy } from '@ngrx/data';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { uniqueId } from 'lodash-es';
import {
  catchError,
  concatMap,
  debounceTime,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  tap,
} from 'rxjs';

import { BlazeTableTriggersService } from '@blaze-dashboard/blaze-table-feature';
import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { RuntimeFacade, WorkflowUtilsService } from '@blaze-runtime/engine-feature';
import {
  EnvironmentName,
  TableInfo,
  TriggerInfo,
  TriggerType,
  WorkflowId,
} from '@blaze-shared/api-contracts-domain';
import { TablesApiService, TablesCollectionService } from '@blaze-shared/datasets-data-access';
import { ActivatedTriggersMap } from '@blaze-shared/datasets-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SharedModule } from '@blaze-shared/shared-feature';
import { ButtonComponent, DialogComponent } from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';
import { Workflow } from '@blaze-shared/workflow-domain';

import { BlazeTableTriggersWarningsComponent } from '../blaze-table-triggers-warnings/blaze-table-triggers-warnings.component';

export interface BlazeTableTriggersDialogData {
  tableId: string;
  triggerWorkflowId?: WorkflowId;
  inputData?: Record<string, unknown>;
}

export interface BlazeTableTriggersDialogResult {
  table$: Observable<TableInfo | null>;
}

type TriggersForm = FormGroup<{ triggers: FormArray<FormGroup<TriggerForm>> }>;

interface TriggerForm {
  workflowId: FormControl<string>;
  triggerType: FormControl<TriggerType>;
  inputData: FormControl<TriggerInfo['inputData']>;
  isActiveDevelopment: FormControl<boolean>;
  isActiveProduction: FormControl<boolean>;
}

type TriggerFormValue = FormGroup<TriggerForm>['value'];

const IS_ACTIVE_CONTROL_NAME: Record<EnvironmentName, keyof TriggerFormValue> = {
  Development: 'isActiveDevelopment',
  Production: 'isActiveProduction',
};

@UntilDestroy()
@Component({
  selector: 'blaze-table-triggers-dialog',
  templateUrl: './blaze-table-triggers-dialog.component.html',
  styleUrls: ['./blaze-table-triggers-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BlazeTableTriggersWarningsComponent,
    ButtonComponent,
    MatIconModule,
    MatSlideToggleModule,
    DialogComponent,
    SharedModule,
  ],
})
export class BlazeTableTriggersDialogComponent {
  private readonly blazeTableTriggersService = inject(BlazeTableTriggersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogData = inject<BlazeTableTriggersDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<BlazeTableTriggersDialogComponent, BlazeTableTriggersDialogResult>>(
      MatDialogRef,
    );
  private readonly fb = inject(FormBuilder);
  private readonly notificationService = inject(NotificationService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly store = inject(Store);
  private readonly tablesApiService = inject(TablesApiService);
  private readonly tablesCollService = inject(TablesCollectionService);
  private readonly workflowUtilsService = inject(WorkflowUtilsService);

  private readonly tableResult$$ = new ReplaySubject<TableInfo | null>(1);

  protected readonly TYPE_OPTIONS: SelectOption<TriggerType>[] = [
    { value: 'Insert', label: 'Inserted' },
    { value: 'Update', label: 'Updated' },
    { value: 'Upsert', label: 'Inserted + Updated' },
    { value: 'Delete', label: 'Deleted' },
  ];

  protected readonly canActivateTrigger = this.blazeTableTriggersService.canActivateTrigger;
  protected readonly environmentName = this.blazeTableTriggersService.environmentName;
  protected readonly isActiveControlName =
    IS_ACTIVE_CONTROL_NAME[this.environmentName || 'Development'];

  protected readonly loading = signal(false);
  protected readonly busy = signal(false);
  protected readonly table = signal<TableInfo | null>(null);
  protected readonly activatedMap = signal<ActivatedTriggersMap>({});
  protected readonly workflowsMap = signal<Record<WorkflowId, Workflow>>({});
  protected readonly cycles = signal<Set<WorkflowId>>(new Set());

  protected form: TriggersForm | undefined = undefined;
  protected formId = uniqueId('triggers-dialog-form');

  protected get triggerControls(): FormArray<FormGroup<TriggerForm>> | undefined {
    return this.form?.controls.triggers;
  }

  protected get canRemove(): boolean {
    const controls = this.triggerControls?.controls || [];
    return (
      !((controls?.length ?? 0) === 1 && !!controls?.[0].pristine) &&
      !this.singleTriggerModeWorkflowId
    );
  }

  protected get singleTriggerModeWorkflowId(): string | undefined {
    return this.dialogData.triggerWorkflowId;
  }

  protected get isSingleTriggerMode(): boolean {
    return !!this.dialogData.triggerWorkflowId;
  }

  constructor() {
    if (!this.dialogData.tableId) {
      throw new Error('Table ID is required to show triggers dialog.');
    }

    this.setup()
      .pipe(
        untilDestroyed(this),
        switchMap(({ table, form }) =>
          this.observeChanges(table, form).pipe(
            finalize(() => {
              this.removePristineTriggersWorkflows(this.form);
              this.tableResult$$.next(this.table());
              this.tableResult$$.complete();
            }),
          ),
        ),
        catchError((error) => {
          this.notificationService.showError(error);
          return of(null);
        }),
      )
      .subscribe();

    this.dialogRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.closeDialog();
      });
  }

  protected requestAddTrigger(): void {
    const table = this.table();
    if (!table) {
      return;
    }

    this.busy.set(true);
    this.addTrigger(table)
      .pipe(untilDestroyed(this))
      .subscribe((triggerControls) => {
        if (triggerControls) {
          this.triggerControls?.push(triggerControls);
        }
        this.busy.set(false);
      });
  }

  protected requestRemoveTrigger(index: number): void {
    const triggerFormValue = this.triggerControls?.at(index)?.value;
    if (!triggerFormValue) {
      return;
    }

    const workflowId = triggerFormValue.workflowId;
    const isActive = !!triggerFormValue[this.isActiveControlName];
    const isPristine = !!this.triggerControls?.at(index)?.pristine;

    if (!workflowId) {
      return;
    }

    this.busy.set(true);
    this.blazeTableTriggersService
      .promptRemoveTrigger(isActive)
      .pipe(
        untilDestroyed(this),
        switchMap((confirm) => {
          if (!confirm) {
            return of(false);
          }
          return this.blazeTableTriggersService.postRemoveWorkflow(workflowId, isPristine).pipe(
            switchMap(() => {
              this.triggerControls?.removeAt(index);
              const table = this.table();
              if (this.triggerControls?.length === 0 && table) {
                return this.addTrigger(table).pipe(
                  map((triggerControls) => {
                    if (triggerControls) {
                      this.triggerControls?.push(triggerControls);
                    }
                    return true;
                  }),
                );
              }

              return of(true);
            }),
          );
        }),
      )
      .subscribe(() => {
        this.busy.set(false);
      });
  }

  protected requestEditWorkflow(index: number): void {
    const workflowId = this.triggerControls?.at(index)?.value.workflowId;
    if (!workflowId) {
      return;
    }

    this.store.dispatch(
      runtimeActions.editElementWorkflow({ workflowId, inputData: this.dialogData.inputData }),
    );
  }

  protected changeTriggerType(triggerType: TriggerType | TriggerType[], index: number): void {
    const triggerFormValue = this.triggerControls?.at(index)?.value;
    if (!triggerFormValue) {
      return;
    }

    let isActive = triggerFormValue[this.isActiveControlName];

    if (isActive) {
      const canBeActivated = this.canActivateTrigger(
        triggerFormValue?.workflowId,
        triggerFormValue?.triggerType,
        this.activatedMap(),
      );

      if (!canBeActivated) {
        isActive = false;
      }
    }

    this.triggerControls?.at(index)?.patchValue({
      ...triggerFormValue,
      [this.isActiveControlName]: isActive,
      triggerType: Array.isArray(triggerType) ? triggerType[0] : triggerType,
    });
  }

  protected isWorkflowConfigured(workflow: Workflow | undefined): boolean {
    return !!workflow && !workflow.isDraft && !!workflow.startNodeId && workflow.nodes.length > 0;
  }

  protected closeDialog(): void {
    this.dialogRef.close({ table$: this.tableResult$$.asObservable() });
  }

  private setup(): Observable<{
    table: TableInfo;
    form: TriggersForm;
  }> {
    this.loading.set(true);

    return this.tablesCollService
      .getByKey(this.dialogData.tableId, { mergeStrategy: MergeStrategy.IgnoreChanges })
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        switchMap((table) => {
          const form = this.createForm(table.triggers ?? []);
          if (form.value.triggers?.length === 0) {
            return this.addTrigger(table).pipe(
              map((triggerControls) => {
                if (triggerControls) {
                  form.controls.triggers.push(triggerControls);
                }
                return { table, form };
              }),
            );
          }
          return of({ table, form });
        }),
        concatLatestFrom(() => this.runtimeFacade.workflows$),
        map(([{ table, form }, workflows]) => ({
          table,
          form,
          workflows,
        })),
        tap(({ table, form, workflows }) => {
          this.table.set(table);
          this.form = form;
          this.updateActivatedMap(form);
          this.updateWorkflowsMap(form, workflows);
          this.updateCycles(table.triggers);
          this.updateControls(form);
          this.loading.set(false);
          this.cdr.markForCheck();
        }),
      );
  }

  private observeChanges(table: TableInfo, form: TriggersForm): Observable<TableInfo | null> {
    const valueChanges$ = form.valueChanges.pipe(
      debounceTime(100),
      map(() => {
        const changes = this.form?.controls.triggers.controls
          .filter((control) => !control.pristine)
          .map((control) => control.value);

        const freshTable = this.table();
        if (!freshTable) {
          throw new Error('Table is required to update triggers.');
        }
        if (!changes) {
          throw new Error('Form has no triggers to update.');
        }
        return this.convertFormToTriggerInfo(changes, this.cycles());
      }),
      tap(() => {
        this.updateActivatedMap(form);
        this.updateControls(form);
      }),
      concatMap((triggers) =>
        this.tablesApiService.setTriggers(table.id, triggers).pipe(
          catchError((error) => {
            this.notificationService.showError(error);
            return of(null);
          }),
        ),
      ),
    );

    const workflowEdits$ = this.workflowUtilsService.finishedEditingWorkflow().pipe(
      map(({ workflowId }) =>
        this.triggerControls?.controls.find((control) => control.value.workflowId === workflowId),
      ),
      filter(Boolean),
      switchMap((control) => {
        const tableId = this.table()?.id;
        if (!tableId) {
          return EMPTY;
        }

        // Make sure that freshly added triggers will be marked as touched when finished editing a workflow.
        if (control.pristine) {
          control.markAsDirty();
          control.updateValueAndValidity({ emitEvent: true });
        }

        return this.tablesApiService.postTriggersCycles(tableId).pipe(
          catchError((error) => {
            this.notificationService.showError(error);
            return of(null);
          }),
        );
      }),
    );

    return merge(valueChanges$, workflowEdits$).pipe(
      untilDestroyed(this),
      concatLatestFrom(() => this.runtimeFacade.workflows$),
      tap(([updatedTable, workflows]) => {
        if (updatedTable) {
          this.table.set(updatedTable);
          this.updateActivatedMap(form);
          this.updateWorkflowsMap(form, workflows);
          this.updateCycles(updatedTable.triggers);
          this.updateControls(form);
        }
      }),
      map(([table]) => table),
    );
  }

  private createForm(triggers: TriggerInfo[]): TriggersForm {
    const triggersForm = this.fb.array<FormGroup<TriggerForm>>(
      triggers.map((trigger) => this.createTriggerFormControls(trigger, true)),
    );

    return this.fb.group({ triggers: triggersForm });
  }

  private createTriggerFormControls(trigger: TriggerInfo, dirty = false): FormGroup<TriggerForm> {
    const { workflowId, triggerType, inputData } = trigger;
    const isActiveNonEnv = this.blazeTableTriggersService.isTriggerActive(trigger, null);
    const isActiveDevelopment = this.blazeTableTriggersService.isTriggerActive(
      trigger,
      'Development',
    );
    const isActiveProduction = this.blazeTableTriggersService.isTriggerActive(
      trigger,
      'Production',
    );

    const control = this.fb.group<TriggerForm>({
      workflowId: this.fb.nonNullable.control(workflowId, Validators.required),
      triggerType: this.fb.nonNullable.control(triggerType, Validators.required),
      inputData: this.fb.nonNullable.control(inputData, Validators.required),
      isActiveDevelopment: this.fb.nonNullable.control(
        isActiveDevelopment || isActiveNonEnv,
        Validators.required,
      ),
      isActiveProduction: this.fb.nonNullable.control(isActiveProduction, Validators.required),
    });

    if (dirty) {
      control.markAsDirty({ emitEvent: false });
    }

    return control;
  }

  private convertFormToTriggerInfo(
    formArray: TriggerFormValue[],
    cycles: Set<WorkflowId>,
  ): TriggerInfo[] {
    const result: TriggerInfo[] = [];

    formArray.forEach((value) => {
      const { workflowId, triggerType, inputData, isActiveDevelopment, isActiveProduction } = value;

      if (!workflowId || !triggerType || !inputData) {
        throw new Error('Something went wrong. Variables should contain data at this point.');
      }

      result.push({
        workflowId,
        triggerType,
        inputData,
        cycleDetected: cycles.has(workflowId),
        states: this.blazeTableTriggersService.createStates(
          isActiveDevelopment,
          isActiveProduction,
        ),
      });
    });

    return result;
  }

  private updateActivatedMap(form: TriggersForm): void {
    this.activatedMap.set(
      form.controls.triggers.controls.reduce<ActivatedTriggersMap>((acc, control) => {
        const { triggerType, workflowId } = control.value;

        if (control.value[this.isActiveControlName] && triggerType && workflowId) {
          acc[triggerType] = workflowId;
        }
        return acc;
      }, {}),
    );
  }

  private updateWorkflowsMap(form: TriggersForm | undefined, workflows: Workflow[]): void {
    const triggers = form?.value.triggers || [];
    if (!triggers.length) {
      this.workflowsMap.set({});
      return;
    }

    const result = workflows.reduce<Record<WorkflowId, Workflow>>((acc, workflow) => {
      acc[workflow.id] = workflow;
      return acc;
    }, {});
    this.workflowsMap.set(result);
  }

  private updateCycles(triggers: TriggerInfo[] = []): void {
    this.cycles.update((cycles) => {
      cycles.clear();

      triggers.forEach((trigger) => {
        if (trigger.cycleDetected && trigger.workflowId) {
          cycles.add(trigger.workflowId);
        }
      });
      return cycles;
    });
  }

  private updateControls(form: TriggersForm): void {
    const activatedMap = this.activatedMap();
    form.controls.triggers.controls.forEach((control) => {
      const { workflowId, triggerType } = control.value;
      const canEnable = this.canActivateTrigger(workflowId, triggerType, activatedMap);
      if (canEnable) {
        control.get(this.isActiveControlName)?.enable({ emitEvent: false, onlySelf: true });
      } else {
        control.get(this.isActiveControlName)?.disable({ emitEvent: false, onlySelf: true });
      }
    });
  }

  private addTrigger(table: TableInfo): Observable<FormGroup<TriggerForm> | null> {
    return this.blazeTableTriggersService.createNewTrigger(table).pipe(
      map((trigger) => {
        if (!trigger) {
          return null;
        }

        return this.createTriggerFormControls(trigger);
      }),
    );
  }

  private removePristineTriggersWorkflows(form?: TriggersForm): void {
    const workflowIdsToRemove: WorkflowId[] = [];

    form?.controls.triggers.controls.forEach((triggerControl) => {
      if (triggerControl.pristine && triggerControl.value.workflowId) {
        workflowIdsToRemove.push(triggerControl.value.workflowId);
      }
    });

    this.blazeTableTriggersService
      .removePristineWorkflows(workflowIdsToRemove)
      .pipe(untilDestroyed(this))
      .subscribe((removed) => console.log('Removed pristine triggers:', removed));
  }
}
